<template>
  <!-- Inline: a collapsible row in the mobile off-canvas menu, laid out like the
       Support accordion beside it rather than a floating dropdown. -->
  <div
    v-if="availableLocales.length > 1 && variant === 'inline'"
    class="locale-switcher locale-switcher--inline"
  >
    <button
      :id="buttonId"
      type="button"
      class="nav-link text-light d-flex align-items-center justify-content-between w-100 locale-switcher__row"
      :disabled="saving"
      :aria-expanded="open ? 'true' : 'false'"
      :aria-controls="`${buttonId}-list`"
      @click="open = !open"
    >
      <span class="d-flex align-items-center">
        <span class="material-icons mr-2" aria-hidden="true">language</span>
        {{ t('nav.language', 'Language') }}
      </span>
      <span class="d-flex align-items-center gap-2">
        <span class="locale-switcher__flag" v-html="flagFor(currentLocale)"></span>
        <span class="locale-switcher__current">{{ currentName }}</span>
        <span class="material-icons locale-switcher__chevron" aria-hidden="true">expand_more</span>
      </span>
    </button>
    <ul v-show="open" :id="`${buttonId}-list`" class="list-unstyled ps-4 mb-1">
      <li v-for="option in availableLocales" :key="option.code">
        <button
          type="button"
          class="nav-link text-light locale-switcher__item"
          :class="{ active: option.code === currentLocale }"
          :lang="option.code"
          :aria-current="option.code === currentLocale ? 'true' : undefined"
          @click="changeLocale(option.code)"
        >
          <span class="locale-switcher__flag" v-html="flagFor(option.code)"></span>
          <span class="locale-switcher__name">{{ option.name }}</span>
          <span
            v-if="option.code === currentLocale"
            class="material-icons locale-switcher__check"
            aria-hidden="true"
          >check</span>
        </button>
      </li>
    </ul>
  </div>

  <div
    v-else-if="availableLocales.length > 1"
    ref="root"
    class="locale-switcher dropdown"
    @keydown.esc="close"
  >
    <button
      :id="buttonId"
      type="button"
      class="locale-switcher__toggle nav-link"
      :class="{ 'is-saving': saving }"
      :disabled="saving"
      aria-haspopup="true"
      :aria-expanded="open ? 'true' : 'false'"
      :aria-label="`${t('nav.language', 'Language')}: ${currentName}`"
      @click="open = !open"
    >
      <span class="locale-switcher__flag" v-html="flagFor(currentLocale)"></span>
      <span class="locale-switcher__code">{{ currentLocale.toUpperCase() }}</span>
      <span class="material-icons locale-switcher__chevron" aria-hidden="true">expand_more</span>
    </button>

    <ul
      class="dropdown-menu dropdown-menu-dark dropdown-menu-end locale-switcher__menu"
      :class="{ show: open }"
      :aria-labelledby="buttonId"
    >
      <li v-for="option in availableLocales" :key="option.code">
        <button
          type="button"
          class="dropdown-item locale-switcher__item"
          :class="{ active: option.code === currentLocale }"
          :lang="option.code"
          :aria-current="option.code === currentLocale ? 'true' : undefined"
          @click="changeLocale(option.code)"
        >
          <span class="locale-switcher__flag" v-html="flagFor(option.code)"></span>
          <span class="locale-switcher__name">{{ option.name }}</span>
          <span
            v-if="option.code === currentLocale"
            class="material-icons locale-switcher__check"
            aria-hidden="true"
          >check</span>
        </button>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import axiosClient from '../../utils/helpers/axios_client';
import { supportedLocales } from '../../i18n/supportedLocales';
import { flagCountryFor, flagSvg } from './flags';

const { locale, t } = useI18n();

// Rails publishes the list; the component never carries its own copy.
const availableLocales = supportedLocales();

// 'dropdown' for the desktop navbar, 'inline' for the mobile off-canvas menu.
const props = withDefaults(defineProps<{ variant?: 'dropdown' | 'inline' }>(), {
  variant: 'dropdown',
});

// Both variants can be on the page at once, so the ids must differ.
const buttonId = `locale-switcher-${props.variant}`;
// i18n/index.ts already resolved the starting locale from what Rails stamped on
// the page, so read it from there rather than re-deriving it here and risking
// the menu disagreeing with the page it sits on.
const currentLocale = ref(locale.value);
const saving = ref(false);
const open = ref(false);
const root = ref<HTMLElement | null>(null);

const currentName = computed(
  () => availableLocales.find((l) => l.code === currentLocale.value)?.name ?? currentLocale.value,
);

// A language without a flag gets a neutral globe rather than a wrong flag.
const GLOBE = '<span class="material-icons" aria-hidden="true">language</span>';
const flagFor = (code: string): string => {
  const country = flagCountryFor(code);
  return country ? flagSvg(country) : GLOBE;
};

const close = () => {
  open.value = false;
};

const onDocumentClick = (event: MouseEvent) => {
  if (root.value && !root.value.contains(event.target as Node)) close();
};

// Only the floating dropdown needs dismissing; the inline list is part of the menu.
onMounted(() => {
  if (props.variant === 'dropdown') document.addEventListener('click', onDocumentClick);
});
onBeforeUnmount(() => document.removeEventListener('click', onDocumentClick));

const changeLocale = async (next: string) => {
  close();
  const previous = locale.value;
  if (next === previous) return;

  currentLocale.value = next;
  saving.value = true;

  try {
    // Persist first. Vue-rendered copy switches instantly, but everything Rails
    // rendered only changes on the next request — so if the write fails there is
    // nothing to reload into and a half-switched page is worse than no switch.
    await axiosClient.post('/set_locale', { locale: next });
  } catch (error) {
    console.error('Failed to change locale:', error);
    currentLocale.value = previous;
    saving.value = false;
    return;
  }

  locale.value = next;
  document.documentElement.lang = next;
  window.location.reload();
};
</script>

<style scoped>
.locale-switcher {
  position: relative;
}

.locale-switcher .locale-switcher__toggle {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 50px;
  color: inherit;
  padding: 0.35rem 0.5rem 0.35rem 0.65rem;
  font-size: 0.875rem;
  font-weight: 600;
  letter-spacing: 0.03em;
  cursor: pointer;
  transition: background-color 0.2s, border-color 0.2s;
}

.locale-switcher__toggle:hover,
.locale-switcher__toggle[aria-expanded='true'] {
  background-color: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.15);
}

.locale-switcher__toggle:focus-visible {
  outline: 2px solid #ffdf00;
  outline-offset: 2px;
}

.locale-switcher__toggle.is-saving {
  opacity: 0.6;
  cursor: progress;
}

.locale-switcher__chevron {
  font-size: 1.1rem;
  opacity: 0.7;
  transition: transform 0.2s;
}

.locale-switcher__toggle[aria-expanded='true'] .locale-switcher__chevron {
  transform: rotate(180deg);
}

.locale-switcher__flag {
  display: inline-flex;
  flex-shrink: 0;
  width: 21px;
  height: 14px;
  border-radius: 2px;
  overflow: hidden;
  /* Keeps the white bands of FR/IE/IT/NL visible against a light menu hover. */
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.15);
}

.locale-switcher__flag :deep(svg) {
  width: 100%;
  height: 100%;
  display: block;
}

.locale-switcher__flag :deep(.material-icons) {
  font-size: 14px;
  line-height: 14px;
  margin: 0 auto;
}

/* Doubled class: a global .dropdown-menu rule caps menus at 250px and scrolls,
   which hid the last two languages. The list is short, so show it whole. */
.locale-switcher .locale-switcher__menu.locale-switcher__menu {
  max-height: none;
  overflow: visible;
  right: 0;
  left: auto;
  min-width: 11rem;
  padding: 0.35rem;
  border-radius: 0.5rem;
}

.locale-switcher__item {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  width: 100%;
  border-radius: 0.35rem;
  padding: 0.45rem 0.65rem;
}

.locale-switcher__name {
  flex: 1;
}

.locale-switcher__check {
  font-size: 1.1rem;
  color: #ffdf00;
}

.locale-switcher__row {
  background: transparent;
  border: 0;
  text-align: left;
}

.locale-switcher__current {
  font-size: 0.875rem;
  opacity: 0.7;
}

.locale-switcher__row .locale-switcher__chevron {
  font-size: 24px;
  opacity: 1;
}

.locale-switcher__row[aria-expanded='true'] .locale-switcher__chevron {
  transform: rotate(180deg);
}

.locale-switcher--inline .locale-switcher__item {
  background: transparent;
  border: 0;
  text-align: left;
  padding-left: 0;
}

.locale-switcher__item.active {
  background-color: rgba(255, 255, 255, 0.08);
  color: #fff;
}
</style>
